import { ArrowRight, ChevronDown, Sparkles, Package, Truck, Heart, Star, Quote } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { IMAGES, PRODUCTS, SIGNATURE_IDS, REVIEWS, GALLERY_IMAGES, getProductById } from "../data/products";
import ProductCard from "../components/ProductCard";
import Reveal from "../components/Reveal";
import Sparkle from "../components/Sparkle";
import { useEffect, useState } from "react";
import useSiteImages from "../hooks/useSiteImages";
import { API, resolveImageUrl } from "../lib/api";

const resolveProductImage = resolveImageUrl;

const Stars = ({ n = 5 }) => (
  <div className="flex gap-0.5 text-shay-gold">
    {Array.from({ length: n }).map((_, i) => (
      <Star key={i} size={14} fill="currentColor" strokeWidth={0} />
    ))}
  </div>
);

export default function Home() {
  const [products, setProducts] = useState(PRODUCTS);
  const featured = products.slice(0, 6);
  const signatures = SIGNATURE_IDS.map((id) => products.find((p) => p.id === id) || getProductById(id)).filter(Boolean);
  const [lightbox, setLightbox] = useState(null);
  const siteImages = useSiteImages();
  const galleryImages = GALLERY_IMAGES.map((fallback, index) => siteImages[`gallery_${index + 1}`] || fallback);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/products`);
        if (mounted && Array.isArray(data) && data.length > 0) {
          setProducts(data.map((product) => ({
            ...product,
            image: resolveProductImage(product.image),
          })));
        }
      } catch (e) {
        // Keep static fallback products if the API is unavailable.
      }
    })();
    return () => { mounted = false; };
  }, []);

  return (
    <div data-testid="home-page">
      {/* ============ HERO ============ */}
      <section className="relative min-h-screen flex items-end overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={siteImages.home_hero || IMAGES.chocolateChipHero}
            alt="Fresh baked cookies"
            className="w-full h-full object-cover scale-105"
            fetchpriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-shay-navy via-shay-navy/70 to-shay-navy/30" />
          <div className="absolute inset-0 bg-gradient-to-r from-shay-navy/80 via-transparent to-transparent" />
        </div>

        <div className="relative w-full max-w-7xl mx-auto px-6 md:px-8 pb-20 md:pb-32 pt-32">
          {/* Brand sparkles scattered in the hero */}
          <Sparkle size={32} color="#C89A36" className="absolute top-24 right-[12%] opacity-70 hidden md:block animate-float-slow" />
          <Sparkle size={18} color="#C89A36" className="absolute top-44 right-[8%] opacity-60 hidden md:block" />
          <Sparkle size={24} color="#C89A36" className="absolute bottom-32 right-[20%] opacity-50 hidden lg:block animate-float-slow" />

          <div className="max-w-3xl">
            <Reveal>
              <p className="eyebrow eyebrow-gold mb-3 flex items-center gap-2">
                <Sparkle size={11} color="#C89A36" />
                Shay Cooks LLC · Orlando, FL
              </p>
            </Reveal>
            <Reveal delay={0.05}>
              <p
                className="font-script text-shay-gold text-5xl sm:text-6xl md:text-7xl leading-none mb-4 md:mb-5 inline-flex items-center gap-3 md:gap-4 hero-shadow"
                data-testid="hero-tagline"
              >
                <Sparkle size={20} color="#C89A36" className="opacity-80" />
                Did You Eat?
                <Sparkle size={20} color="#C89A36" className="opacity-80" />
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <h1 className="font-display text-shay-cream text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] leading-[0.95] tracking-tight hero-shadow">
                Fresh Baked
                <br />
                <span className="italic text-shay-gold">Happiness</span> Delivered.
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-7 text-shay-cream/85 text-lg md:text-xl max-w-xl leading-relaxed">
                Small batch gourmet cookies and brownies baked with love and delivered fresh to your doorstep.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/menu" className="btn-gold" data-testid="hero-order-cookies-btn">
                  Order Cookies <ArrowRight size={16} />
                </Link>
                <Link to="/menu" className="btn-outline-cream" data-testid="hero-view-menu-btn">
                  View Menu
                </Link>
              </div>
            </Reveal>
          </div>

          <div className="hidden md:flex absolute bottom-10 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-shay-cream/70 scroll-indicator">
            <span className="text-[10px] tracking-[0.4em] uppercase">Scroll</span>
            <ChevronDown size={20} strokeWidth={1.5} />
          </div>
        </div>
      </section>

      {/* ============ MARQUEE BAR ============ */}
      <section className="bg-shay-navy text-shay-gold py-5 overflow-hidden border-b border-shay-gold/20">
        <div className="flex gap-16 whitespace-nowrap animate-marquee" data-testid="marquee-bar">
          {Array.from({ length: 2 }).flatMap((_, ii) =>
            ["Small Batch", "Hand Rolled", "Locally Delivered", "Baked Fresh Daily", "Made with Love", "Did You Eat?"].map(
              (w, i) => (
                <span key={`${ii}-${i}`} className="font-display italic text-2xl md:text-3xl tracking-tight flex items-center gap-16">
                  {w}
                  <span className="h-1.5 w-1.5 rounded-full bg-shay-gold" />
                </span>
              )
            )
          )}
        </div>
      </section>

      {/* ============ FEATURED PRODUCTS ============ */}
      <section className="bg-shay-cream py-20 md:py-32 paper-noise relative" data-testid="featured-products-section">
        <div className="floating-cookie -top-10 left-20 w-40 h-40 animate-float-slow" />
        <div className="floating-cookie bottom-20 right-10 w-32 h-32 animate-float-slow" />

        <div className="max-w-7xl mx-auto px-6 md:px-8 relative">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
              <div>
                <p className="eyebrow">The Lineup</p>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-shay-navy leading-[0.95] mt-3 tracking-tight">
                  Treats baked
                  <br /> with <span className="italic text-shay-brown">soul.</span>
                </h2>
              </div>
              <Link to="/menu" className="btn-ghost-navy self-start md:self-end" data-testid="featured-view-all-btn">
                View Full Menu <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
            {featured.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.05}>
                <ProductCard product={p} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SIGNATURE FAVORITES ============ */}
      <section className="bg-shay-cream-deep py-20 md:py-32" data-testid="signature-favorites-section">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <Reveal>
            <div className="text-center mb-20 max-w-2xl mx-auto">
              <p className="eyebrow">Signature Favorites</p>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-shay-navy mt-3 tracking-tight">
                The ones we're <span className="italic text-shay-gold">famous</span> for.
              </h2>
            </div>
          </Reveal>

          <div className="space-y-28 md:space-y-40">
            {signatures.map((p, i) => {
              const reverse = i % 2 === 1;
              return (
                <Reveal key={p.id}>
                  <div
                    className={`grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center ${
                      reverse ? "md:[&>*:first-child]:order-2" : ""
                    }`}
                  >
                    <div className="md:col-span-7 relative zoom-on-hover aspect-[5/4] bg-shay-cream">
                      <img src={p.image} alt={p.name} className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async" />
                      <span className="absolute -top-6 left-6 md:left-10 font-script text-shay-gold text-[5rem] md:text-[7rem] leading-none drop-shadow-lg">
                        0{i + 1}
                      </span>
                    </div>
                    <div className="md:col-span-5">
                      <p className="eyebrow">{p.category === "cookies" ? "The Cookie" : "The Brownie"}</p>
                      <h3 className="font-display text-3xl md:text-5xl text-shay-navy mt-3 leading-[1.05] tracking-tight">
                        {p.name}
                      </h3>
                      <div className="mt-6 h-px w-16 bg-shay-gold" />
                      <p className="mt-6 text-shay-chocolate/80 text-lg leading-relaxed">{p.description}</p>
                      <div className="mt-8 flex items-center gap-6">
                        <p className="font-display text-3xl text-shay-navy">${p.price.toFixed(2)}</p>
                        <Link to="/menu" className="btn-ghost-navy" data-testid={`signature-shop-${p.id}`}>
                          Shop {p.category === "cookies" ? "Cookies" : "Brownies"}
                        </Link>
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ DELIVERY SECTION ============ */}
      <section className="bg-shay-cream py-20 md:py-28 paper-noise relative" data-testid="delivery-section">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <p className="eyebrow">From Our Kitchen To You</p>
              <h2 className="font-display text-4xl md:text-5xl text-shay-navy mt-3 tracking-tight">
                Three things we promise.
              </h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16">
            {[
              { icon: Sparkles, title: "Freshly Baked", body: "Every order is baked the day it ships — never frozen, never stale, always small batch." },
              { icon: Package, title: "Packaged Carefully", body: "Hand-wrapped in food-safe parchment and tucked into our signature gold + navy boxes." },
              { icon: Truck, title: "Delivered Locally", body: "Local Orlando delivery, straight from our kitchen to your doorstep — usually within 24 hours." },
            ].map((s, i) => (
              <Reveal key={s.title} delay={i * 0.1}>
                <div className="text-center md:text-left">
                  <div className="inline-grid place-items-center h-16 w-16 rounded-full bg-shay-navy text-shay-gold mb-6">
                    <s.icon size={26} strokeWidth={1.4} />
                  </div>
                  <h3 className="font-display text-3xl text-shay-navy tracking-tight">{s.title}</h3>
                  <p className="mt-4 text-shay-chocolate/75 leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CUSTOMER LOVE ============ */}
      <section className="bg-shay-navy text-shay-cream py-20 md:py-32 relative overflow-hidden" data-testid="customer-love-section">
        <div className="floating-cookie top-10 right-20 w-48 h-48 animate-float-slow" />
        <div className="floating-cookie bottom-20 left-10 w-40 h-40 animate-float-slow" />

        <div className="max-w-7xl mx-auto px-6 md:px-8 relative">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
              <div>
                <p className="eyebrow eyebrow-gold">Customer Love</p>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-3 tracking-tight">
                  Words that taste <span className="italic text-shay-gold">sweet.</span>
                </h2>
              </div>
              <Link to="/reviews" className="btn-outline-cream self-start md:self-end" data-testid="reviews-view-all-btn">
                Read All Reviews <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {REVIEWS.slice(0, 3).map((r, i) => (
              <Reveal key={r.id} delay={i * 0.1}>
                <div className="border border-shay-gold/30 p-7 md:p-9 h-full bg-shay-navy-soft/30 backdrop-blur-sm" data-testid={`testimonial-${r.id}`}>
                  <Quote size={28} className="text-shay-gold mb-5" strokeWidth={1.2} />
                  <Stars n={r.rating} />
                  <h3 className="font-display text-xl mt-4 leading-snug">{r.title}</h3>
                  <p className="mt-3 text-shay-cream/75 leading-relaxed text-sm">{r.body}</p>
                  <div className="mt-7 pt-5 border-t border-shay-gold/20 flex items-center justify-between">
                    <p className="font-medium tracking-wide">{r.name}</p>
                    <p className="text-[11px] uppercase tracking-widest text-shay-cream/50">{r.date}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ INSTAGRAM GALLERY ============ */}
      <section className="bg-shay-cream py-20 md:py-28" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-6 md:px-8 mb-12">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <div>
                <p className="eyebrow">@ShayCooksllc</p>
                <h2 className="font-display text-4xl md:text-5xl text-shay-navy mt-3 tracking-tight">
                  Caught on <span className="italic text-shay-brown">camera.</span>
                </h2>
              </div>
              <a
                href="https://instagram.com/Shaycooksllc"
                target="_blank"
                rel="noreferrer"
                className="btn-ghost-navy self-start md:self-end"
                data-testid="instagram-follow-btn"
              >
                Follow on Instagram <ArrowRight size={16} />
              </a>
            </div>
          </Reveal>
        </div>
        <div className="max-w-[1600px] mx-auto px-3 md:px-6">
          <div className="masonry" data-testid="instagram-masonry">
            {galleryImages.map((src, i) => (
              <button
                key={i}
                onClick={() => setLightbox(src)}
                data-testid={`gallery-item-${i}`}
                className="block w-full zoom-on-hover relative group"
                style={{ aspectRatio: i % 3 === 0 ? "1/1" : i % 3 === 1 ? "4/5" : "3/4" }}
              >
                <img src={src} alt="Shay Cooks gallery" loading="lazy" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-shay-navy/0 group-hover:bg-shay-navy/40 transition-colors duration-300 grid place-items-center">
                  <Heart className="text-shay-cream opacity-0 group-hover:opacity-100 transition-opacity duration-300" size={32} fill="currentColor" />
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CTA BANNER ============ */}
      <section className="bg-shay-navy text-shay-cream py-24 md:py-36 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0">
          <img src={siteImages.home_cta || IMAGES.handsBreaking} alt="" className="w-full h-full object-cover opacity-25" loading="lazy" decoding="async" />
          <div className="absolute inset-0 bg-gradient-to-br from-shay-navy via-shay-navy/85 to-shay-navy/50" />
        </div>
        {/* Sparkles scattered like the brand logo */}
        <Sparkle size={36} color="#C89A36" className="absolute top-16 left-[8%] opacity-70 animate-float-slow" />
        <Sparkle size={20} color="#C89A36" className="absolute top-32 left-[20%] opacity-60" />
        <Sparkle size={28} color="#C89A36" className="absolute bottom-20 left-[12%] opacity-50 animate-float-slow" />
        <Sparkle size={32} color="#C89A36" className="absolute top-20 right-[10%] opacity-70 animate-float-slow" />
        <Sparkle size={18} color="#C89A36" className="absolute top-44 right-[22%] opacity-60" />
        <Sparkle size={24} color="#C89A36" className="absolute bottom-24 right-[8%] opacity-50 animate-float-slow" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-8 text-center">
          <Reveal>
            <p className="eyebrow eyebrow-gold inline-flex items-center gap-2">
              <Sparkle size={11} color="#C89A36" /> Did You Eat? <Sparkle size={11} color="#C89A36" />
            </p>
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl mt-4 tracking-tight leading-[0.95]">
              Ready For Something
              <br />
              <span className="italic text-shay-gold">Sweet?</span>
            </h2>
            <p className="mt-7 text-shay-cream/80 text-lg max-w-xl mx-auto leading-relaxed">
              Skip the supermarket aisle. Order small batch cookies and brownies baked fresh today.
            </p>
            <Link to="/menu" className="btn-gold mt-10" data-testid="cta-order-now-btn">
              Order Now <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[80] bg-shay-navy/95 grid place-items-center p-6 cursor-zoom-out"
          onClick={() => setLightbox(null)}
          data-testid="lightbox-overlay"
        >
          <img src={lightbox} alt="Shay Cooks gallery" className="max-w-full max-h-full object-contain" loading="lazy" decoding="async" />
        </div>
      )}
    </div>
  );
}
